import { v4 as uuid } from 'uuid'
import Cookies from 'js-cookie'
import { addNewFormEntry, errorLogger, getLocale, tracking, translatePage } from '../../common/script'
import {
    E4K_GAME_ID,
    LINKS,
    RANCH_WEB_SHOP_CALL,
    TED_MOBILE_BROWSER_SHOP,
    TrackingPayloadMBS,
    TrackingPayloadRanch,
    MbsSessionData,
} from '../../common/constants'
import availableLanguages, { AvailableLanguages, availableLanguagesArray } from './translations'

export const translation = {
    defaultLanguage: 'en' as AvailableLanguages,
    get language(): AvailableLanguages {
        const userLocale = getLocale()
        const formattedUserLocale = userLocale.toLowerCase().replace('_', '-')

        if (availableLanguagesArray.includes(formattedUserLocale)) {
            return formattedUserLocale as AvailableLanguages
        }

        const userLocaleWithoutRegion = formattedUserLocale.slice(0, 2)
        if (availableLanguagesArray.includes(userLocaleWithoutRegion)) {
            return userLocaleWithoutRegion as AvailableLanguages
        }

        return this.defaultLanguage as AvailableLanguages
    },
    get languageWithoutRegion(): string {
        return this.language.slice(0, 2)
    },
    get texts(): Record<string, string> {
        const isLanguageAvailable = this.language in availableLanguages
        // There's already a validation to handle unsupported languages
        // This one is just one more fallback to english
        if (isLanguageAvailable) {
            return availableLanguages[this.language] as Record<string, string>
        }

        return availableLanguages[this.defaultLanguage] as Record<string, string>
    },
    init() {
        translatePage(this.texts)
        this.translateLegalLinks()
    },
    getTranslatedText(label: string) {
        return this.texts[label]
    },
    translateLegalLinks() {
        elements.termsLink.href = LINKS.getTermsLink(this.languageWithoutRegion)
        elements.legalLink.href = LINKS.getLegalLink(this.languageWithoutRegion)
        elements.supportLink.href = LINKS.getSupportLink(this.languageWithoutRegion, E4K_GAME_ID)
    },
}

const getSessionId = () => {
    const queryParameters = new URLSearchParams(window.location.search)
    const sid = queryParameters.get('sid')
    return sid ?? uuid()
}

/*
    Session ID priority order:
    1. mbsSession cookie
    2. sid query parameter
    3. uuid
*/
let SESSION_ID = getSessionId()

let sharedTrackingData = {
    zoneId: -1,
    networkId: -1,
    instanceId: -1,
    playerId: -1,
}

export type TrackingDataMBS = Omit<TrackingPayloadMBS, 'eventId' | 'date' | 'microtime' | 'sessionId'>
export const trackingMBS = async (trackingData: TrackingDataMBS) => {
    const formattedTrackingData = {
        gameId: E4K_GAME_ID,
        ...sharedTrackingData,
        isAutoLoggedIn: 0,
        ...trackingData,
        eventId: TED_MOBILE_BROWSER_SHOP,
        date: new Date().toUTCString(),
        microtime: Date.now(),
        sessionId: SESSION_ID,
    } as TrackingPayloadMBS

    return tracking(formattedTrackingData)
}

export const trackingRanch = async () => {
    const formattedTrackingData = {
        gameId: E4K_GAME_ID,
        ...sharedTrackingData,
        sessionId: SESSION_ID,
        eventId: RANCH_WEB_SHOP_CALL,
        date: new Date().toUTCString(),
        microtime: Date.now(),
        unixtimeMS: Date.now(),
        sourceId: 'MBS',
    } as TrackingPayloadRanch

    return tracking(formattedTrackingData)
}

export const handleAutoLogin = () => {
    sessionStorage.setItem('autologin', 'false')

    const mbsSessionCookie = Cookies.get('mbsSession')
    if (!mbsSessionCookie) {
        return false
    }

    const mbsSessionData = JSON.parse(decodeURIComponent(mbsSessionCookie)) as MbsSessionData
    const { playerName, zoneId, criteria, sid, playerId, networkId, instanceId, isTempserver } = mbsSessionData
    sharedTrackingData = {
        zoneId: Number(zoneId),
        playerId,
        networkId,
        instanceId,
    }
    SESSION_ID = sid
    switchToAutoLoginMode(playerName, zoneId, criteria, isTempserver)
    sessionStorage.setItem('autologin', 'true')
    return true
}

export const retrieveLastSubmittedCode = () => {
    const sessionCode = sessionStorage.getItem('code')
    if (sessionCode) {
        elements.input.value = sessionCode
    }
}

export const handleErrorResponse = () => {
    const queryParameters = new URLSearchParams(window.location.search)
    const hasError = queryParameters.has('errorStatusCode')
    if (!hasError) {
        return false
    }

    let errorMessage: string

    const errorStatusCode = queryParameters.get('errorStatusCode') ?? ''

    switch (errorStatusCode) {
        case '404': {
            errorMessage = translation.getTranslatedText('webshop_offer_voucher_codeIsIncorrect')
            break
        }
        case '423': {
            errorMessage = translation.getTranslatedText('webShop_accountBanned_desc')
            break
        }
        case '498': {
            errorMessage = translation.getTranslatedText('mbs_access_error_sessionExpired_desc')
            break
        }
        default: {
            errorLogger(`Error returned from validate API: ${errorStatusCode}`)
            errorMessage = translation.getTranslatedText('webShop_unexpectedError_desc')
            break
        }
    }

    displayFeedbackMessage(true, errorMessage, true)
    return true
}

export const elements: {
    feedbackMessage: HTMLSpanElement
    feedbackMessageBox: HTMLDivElement
    input: HTMLInputElement
    codeForm: HTMLFormElement
    autoLoginForm: HTMLFormElement
    btnOk: HTMLButtonElement
    btnContinue: HTMLButtonElement
    btnFindCode: HTMLButtonElement
    findCodeModal: HTMLDivElement
    termsLink: HTMLAnchorElement
    legalLink: HTMLAnchorElement
    supportLink: HTMLAnchorElement
    welcomeTitle: HTMLHeadingElement
} = {
    feedbackMessage: document.querySelector('#feedback-message')!,
    feedbackMessageBox: document.querySelector('#feedback-message-box')!,
    input: document.querySelector('#shoppingCode')!,
    btnOk: document.querySelector('#btn-ok')!,
    btnContinue: document.querySelector('#btn-continue')!,
    codeForm: document.querySelector('#code-form')!,
    autoLoginForm: document.querySelector('#autologin-form')!,
    btnFindCode: document.querySelector('#btn-find-code')!,
    findCodeModal: document.querySelector('#find-code-modal')!,
    termsLink: document.querySelector('#terms-link')!,
    legalLink: document.querySelector('#legal-link')!,
    supportLink: document.querySelector('#support-link')!,
    welcomeTitle: document.querySelector('#welcome-title')!,
}

export const switchToAutoLoginMode = (playerName: string, zoneId: string, criteria: string, isTempserver: boolean) => {
    elements.welcomeTitle.textContent = translation
        .getTranslatedText('mbs_access_welcome_desc')
        .replace('{0}', playerName)
    elements.autoLoginForm.classList.toggle('d-none', false)

    window.addEventListener('load', function () {
        if (typeof window.CanvasAgent?.create === 'function') {
            const tokenCookie = Cookies.get('token')
            if (!tokenCookie) {
                return false
            }
            window.CanvasAgent.create({
                authClientId: 'lemonstand',
                canvasAppId: process.env.CANVAS_APP_ID,
                token: tokenCookie,
                debug: process.env.CANVAS_DEBUG,
                locale: getSupportedIgsLocale(translation.language),
            })
                .then((res) => {
                    log('Canvas agent initiated')
                    window.CanvasAgent.instance = res
                    elements.btnContinue.addEventListener('click', (event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        res.show()
                        return false
                    })
                })
                .catch((error) => {
                    console.error('Cannot create instance of Canvas', error)
                })
        } else {
            // Store the success URL for navigation after tracking
            const successUrl = getSuccessUrl({ zoneId, criteria, isTempserver })
            elements.autoLoginForm.dataset.successUrl = successUrl
        }
    })
}

export const enableCodeForm = () => {
    elements.codeForm.classList.toggle('d-none', false)
}

export const displayFeedbackMessage = (doDisplay: boolean, message = '', isError = false) => {
    elements.input.style.color = isError ? '#FF5666' : '#9FFFCB'
    elements.feedbackMessage.textContent = message
    elements.feedbackMessageBox.classList.toggle('d-none', !doDisplay)
}

export const submitButton = {
    enable() {
        elements.btnOk.removeAttribute('disabled')
        elements.btnOk.classList.remove('disabled')
    },
    disable() {
        elements.btnOk.setAttribute('disabled', 'true')
        elements.btnOk.classList.add('disabled')
    },
    toggle() {
        elements.btnOk.toggleAttribute('disabled')
        elements.btnOk.classList.toggle('disabled')
    },
}

const getSupportedIgsLocale = (language: string) => {
    // Supported chinese variations
    // 'zh-CN', 'zh-Hans', 'zh-Hant', 'zh-TW'
    // TODO: after PMT-2369 is done, we dont need to worry about formatting the locale before sending to IGS

    const igsSpecificLocaleMapper = new Map([
        ['zh', 'zh-Hans'],
        ['zh-cn', 'zh-Hans'],
        ['zh-hans', 'zh-Hans'],
        ['zh-tw', 'zh-Hant'],
        ['zh-hant', 'zh-Hant'],
    ])

    if (igsSpecificLocaleMapper.has(language)) {
        return igsSpecificLocaleMapper.get(language)!
    }

    return translation.languageWithoutRegion
}

export const getSuccessUrl = ({ zoneId = '', criteria = '', isTempserver = false } = {}) => {
    const supportedIgsLocale = getSupportedIgsLocale(translation.language)

    const queryParameters = {
        locale: supportedIgsLocale,
        sid: SESSION_ID,
        standalone: '1',
        callOrigin: window.location.href,
        'lemonstand.customization.url': isTempserver
            ? process.env.E4K_IGS_TEMP_CONFIG_URL!
            : process.env.E4K_IGS_CONFIG_URL!,
        ...(zoneId ? { zoneId } : {}),
        ...(criteria ? { criteria } : {}),
    }

    const formattedParameters = new URLSearchParams(queryParameters).toString()

    // Choose webshop link based on current domain
    // goodgamestudios.com uses _GGS URLs, ggs-ep.com uses _EP URLs
    const isGoodGameDomain = window.location.origin.includes('goodgamestudios.com')
    const baseUrl = isGoodGameDomain ? process.env.WEBSHOP_LINK_GGS! : process.env.WEBSHOP_LINK_EP!

    const webshopLink = `${baseUrl}?${formattedParameters}`

    return webshopLink
}

export const getErrorUrl = () => {
    const queryParameters = {
        lang: translation.language,
        sid: SESSION_ID,
    }

    const formattedParameters = new URLSearchParams(queryParameters).toString()

    const webshopLink = `${window.location.origin}${window.location.pathname}?${formattedParameters}`

    return webshopLink
}

export const prepareFormSubmit = () => {
    // Choose accounts service based on current domain to maintain domain consistency
    // goodgamestudios.com uses _GGS URLs, ggs-ep.com uses _EP URLs
    const isGoodGameDomain = window.location.origin.includes('goodgamestudios.com')
    const accountsServiceUrl = isGoodGameDomain
        ? process.env.VALIDATE_CODE_PAGE_GGS!
        : process.env.VALIDATE_CODE_PAGE_EP!

    elements.codeForm.setAttribute('action', accountsServiceUrl)
    addNewFormEntry('errorUrl', getErrorUrl(), elements.codeForm)
}

export const removeCookie = () => {
    Cookies.remove('mbsSession', { path: '/', domain: '.ggs-ep.com' })
    Cookies.remove('mbsSession', { path: '/', domain: '.goodgamestudios.com' })
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const log = (...args: any[]): void => {
    console.debug('%c CANVAS ->', 'background: pink; color: #bada55, font-size:20px', ...args)
}

export const loadScriptSync = (src: string) => {
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.type = 'module'
    script.async = false
    script.blocking = true
    script.src = src
    const head = document.head
    head.insertBefore(script, head.firstElementChild)
    log('loadScript done')
}
